import React, { memo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Link as RouterLink } from 'react-router-dom';
import PropTypes from 'prop-types';

import CompanyInfo from '@/components/Base/CompanyInfo';
import Icon from '@/components/Base/Icon';
import TBody from '@/components/Base/TBody';
import Tooltip from '@/components/Base/Tooltip/Tooltip';
import ProductActions from '@/components/Pages/Home/ProductActions';
import ProductAdvantages from '@/components/Pages/Home/ProductAdvantages';
import {
  CURRENCIES,
  CURRENCY_EUR,
  CURRENCY_UAH,
  CURRENCY_USD,
} from '@/constants';
import { useBusinessConditionsFieldsItem } from '@/hooks';
import { colorBlack200 } from '@/styleUtils';
import {
  formatCompactParts,
  geProductUrl,
  getGtmEvents,
  getOrganizationUrl,
  getRateDisplay,
} from '@/utils';

import {
  ButtonBlock,
  FoolInfo,
  FoolInfoWrapper,
  MoreInfoAboutBank,
  ProductCharacteristicsBtn,
  ProductCharacteristicsWrapper,
  ProductDetail,
  ProductDetailCaption,
  ProductDetails,
  ProductDetailValue,
  ProductInfo,
  ProductMainInfo,
  ProductOrderBtn,
  ProductOrderBtnCaption,
  ProductOrderBtnCompanyName,
  ProductOrderInfo,
  ProductTitle,
  Table,
  Wrapper,
} from './ProductCard.styles';

export const COMPANY_MONITISATUON_TYPES = {
  link: 'link',
  form: 'form',
};

// eslint-disable-next-line react/require-default-props
function ProductCard(data) {
  const { locale, formatMessage } = useIntl();

  const [showProductCharacteristics, toggleProductCharacteristics] =
    useState(false);

  const {
    productId,
    slug,
    companySlug,
    logoUrl = '',
    companyName,
    trackingUrl = '',
    companyLicense = null,
    companyRating = 0,
    companyRatingType = '',
    loanLimitNumberMax = 0,
    loanLimitNumberMaxUah = 0,
    loanLimitNumberMin = 0,
    loanLimitNumberMinUah = 0,
    currency,
    currencyRate = {},
    loanRateYear = null,
    baseCalculatedRate = null,
    calculatedRateLimit = null,
    periodicalRateCollection = [],
    standardUird = null,
    termMonths = {},
    advantages,
    actions,
    companyNameG,
    productName,
    isRvkPage = false,
  } = data;

  const rateDisplay = getRateDisplay({
    loanRateYear,
    baseCalculatedRate,
    calculatedRateLimit,
    formatMessage,
    periodicalRateCollection,
    standardUird,
  });
  const tableData = useBusinessConditionsFieldsItem({ ...data, rateDisplay });
  const productUrl = geProductUrl({
    org: companySlug,
    id: productId,
    slug,
    currency,
  });
  const organizationUrl = getOrganizationUrl(companySlug);

  const handleToggleCharacteristics = () => {
    toggleProductCharacteristics(!showProductCharacteristics);
  };

  const getDataTypeLink = () => {
    if (trackingUrl) {
      return 'filled';
    }

    return 'empty';
  };

  return (
    <Wrapper>
      <ProductMainInfo>
        <CompanyInfo
          companySlug={companySlug}
          companyName={companyName}
          companyLicense={companyLicense}
          companyRating={companyRating}
          companyRatingType={companyRatingType}
          logoUrl={logoUrl}
        />

        <ProductInfo>
          <ProductTitle>
            {formatMessage(
              { id: 'productCard.title' },
              { productName, companyNameG }
            )}
          </ProductTitle>

          <ProductDetails>
            {(!!loanLimitNumberMax || !!loanLimitNumberMin) && (
              <ProductDetail>
                <ProductDetailValue>
                  {formatMessage(
                    { id: 'productCard.loanLimit' },
                    {
                      ...formatCompactParts(
                        loanLimitNumberMax || loanLimitNumberMin,
                        locale
                      ),
                      currency: CURRENCIES[currency],
                      from: loanLimitNumberMin && !loanLimitNumberMax,
                    }
                  )}

                  {(!!loanLimitNumberMaxUah || !!loanLimitNumberMinUah) && (
                    <Tooltip
                      contentTooltip={formatMessage(
                        { id: 'tooltip.loanLimitNumber' },
                        {
                          ...formatCompactParts(
                            loanLimitNumberMaxUah || loanLimitNumberMinUah,
                            locale
                          ),
                          currency: CURRENCIES.uah,
                          rate: (
                            <span className={`trend${currencyRate?.trend}`}>
                              {currencyRate?.rate}
                            </span>
                          ),
                        }
                      )}
                      topIconStyle="0"
                      icon="info"
                      textColor={colorBlack200}
                    />
                  )}
                </ProductDetailValue>

                <ProductDetailCaption>
                  {formatMessage({ id: 'productCard.loanLimit.label' })}
                </ProductDetailCaption>
              </ProductDetail>
            )}

            {!!rateDisplay?.text && (
              <ProductDetail>
                <Tooltip
                  contentLabel={
                    <ProductDetailValue>{rateDisplay.text}</ProductDetailValue>
                  }
                  contentTooltip={rateDisplay.tooltip}
                  topIconStyle="0"
                  icon="info"
                  textColor={colorBlack200}
                />

                <ProductDetailCaption>
                  {formatMessage({ id: 'productCard.loanRateYear.label' })}
                </ProductDetailCaption>
              </ProductDetail>
            )}

            {!!termMonths?.max && (
              <ProductDetail>
                <ProductDetailValue>
                  {formatMessage(
                    { id: 'productCard.termMonths' },
                    { numberPart: termMonths.max }
                  )}
                </ProductDetailValue>

                <ProductDetailCaption>
                  {formatMessage({ id: 'productCard.termMonths.label' })}
                </ProductDetailCaption>
              </ProductDetail>
            )}
          </ProductDetails>
        </ProductInfo>

        <ProductOrderInfo>
          <ProductOrderBtn
            as={trackingUrl ? 'a' : RouterLink}
            title={companyName}
            target={trackingUrl ? '_blank' : null}
            href={trackingUrl}
            to={productUrl}
            data-type={getDataTypeLink()}
            {...getGtmEvents({
              gtmEl: `${companySlug}`,
              gtmEa: 'order-button',
              gtmEaName: 'credits_order-button',
            })}
          >
            {trackingUrl ? (
              <>
                <ProductOrderBtnCaption>
                  {formatMessage({ id: 'productCard.getCredit' })}
                </ProductOrderBtnCaption>

                <ProductOrderBtnCompanyName>
                  {formatMessage(
                    { id: 'productCard.fromCompany' },
                    { companyName }
                  )}
                </ProductOrderBtnCompanyName>
              </>
            ) : (
              formatMessage({ id: 'productCard.inDetail' })
            )}
          </ProductOrderBtn>

          <ProductCharacteristicsBtn
            type="button"
            onClick={handleToggleCharacteristics}
            title={formatMessage({ id: 'productCard.moreButton' })}
            data-active={showProductCharacteristics}
          >
            {formatMessage({ id: 'productCard.moreButton' })}
          </ProductCharacteristicsBtn>
        </ProductOrderInfo>
      </ProductMainInfo>

      <ButtonBlock>
        {!!advantages.length && <ProductAdvantages advantages={advantages} />}
      </ButtonBlock>

      {showProductCharacteristics && (
        <ProductCharacteristicsWrapper>
          <Table className="customTable">
            <TBody items={tableData} />
          </Table>

          <FoolInfoWrapper>
            <FoolInfo to={productUrl}>
              {formatMessage({ id: 'productCard.foolInfo' })}

              <Icon name="arrowRight" height="8" />
            </FoolInfo>

            {!isRvkPage && (
              <MoreInfoAboutBank to={organizationUrl}>
                {formatMessage(
                  { id: 'productCard.moreInfoAboutBank' },
                  { companyName }
                )}

                <Icon name="link" width="12" height="12" />
              </MoreInfoAboutBank>
            )}
          </FoolInfoWrapper>

          {!!actions.length && <ProductActions actions={actions} />}
        </ProductCharacteristicsWrapper>
      )}
    </Wrapper>
  );
}

ProductCard.propTypes = {
  productId: PropTypes.number.isRequired,
  slug: PropTypes.string.isRequired,
  companyMonetizationType: PropTypes.oneOf([
    COMPANY_MONITISATUON_TYPES.link,
    COMPANY_MONITISATUON_TYPES.form,
  ]),
  companySlug: PropTypes.string.isRequired,
  logoUrl: PropTypes.string,
  companyName: PropTypes.string.isRequired,
  vendorId: PropTypes.number.isRequired,
  requestConsiderationTerm: PropTypes.number,
  companyLicense: PropTypes.string,
  wayToGet: PropTypes.arrayOf(PropTypes.string),
  isPrepayment: PropTypes.bool,
  companyPromoLabel: PropTypes.string,
  effectiveInterestRate: PropTypes.shape({
    min: PropTypes.number,
    max: PropTypes.number,
  }),
  companyRating: PropTypes.string,
  companyRatingType: PropTypes.string,
  repeatConditions: PropTypes.shape({
    repeatedLoanValue: PropTypes.number,
    repeatedTermRange: PropTypes.objectOf(
      PropTypes.shape({ type: PropTypes.string })
    ),
    repeatedRate: PropTypes.number,
    repeatedForClientsOnly: PropTypes.bool,
  }),
  loanLimitNumberMax: PropTypes.number,
  loanLimitNumberMaxUah: PropTypes.number,
  loanLimitNumberMin: PropTypes.number,
  loanLimitNumberMinUah: PropTypes.number,
  currencyRate: PropTypes.shape({
    rate: PropTypes.number,
    trend: PropTypes.string,
  }),
  loanRateYear: PropTypes.number,
  baseCalculatedRate: PropTypes.number,
  calculatedRateLimit: PropTypes.number,
  periodicalRateCollection: PropTypes.arrayOf(PropTypes.any),
  standardUird: PropTypes.number,
  termMonths: PropTypes.shape({
    min: PropTypes.number,
    max: PropTypes.number,
  }),
  productUserWarningUrl: PropTypes.string,
  productEssentialCharacteristicsUrl: PropTypes.string,
  advantages: PropTypes.arrayOf(
    PropTypes.shape({ title: PropTypes.string, description: PropTypes.string })
      .isRequired
  ).isRequired,
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      description: PropTypes.string,
      validFrom: PropTypes.string,
      validTo: PropTypes.string,
    }).isRequired
  ).isRequired,
  productName: PropTypes.string.isRequired,
  companyNameG: PropTypes.string.isRequired,
  trackingUrl: PropTypes.string,
  isRvkPage: PropTypes.bool,
  currency: PropTypes.oneOf([CURRENCY_UAH, CURRENCY_USD, CURRENCY_EUR]),
};

export default memo(ProductCard);
